import { useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, Plus, RefreshCw, UserPlus } from 'lucide-react';

export default function AdminExcelUpload() {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only .xlsx files are supported');
      return;
    }
    setSelectedFile(file);
    setResult(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/orders/upload-excel/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse Excel file');
      setPreview(null);
    }
    setUploading(false);
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;
    setConfirming(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/orders/upload-excel/confirm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setPreview(null);
      const total = res.data.created + res.data.updated;
      toast.success(`${total} orders processed (${res.data.created} new, ${res.data.updated} updated)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setConfirming(false);
  };

  const reset = () => {
    setPreview(null);
    setResult(null);
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <DashboardLayout>
      <div data-testid="excel-upload-page" className="space-y-8 max-w-5xl">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Upload Orders (Excel)</h1>
          <p className="text-sm text-[#52525B] mt-1">
            Upload your Excel master sheet. New POs will be created, existing POs will be updated automatically.
          </p>
          <p className="text-xs text-[#A1A1AA] mt-2">
            Required columns: PO, STYLE NAME, PA NO, DEPART, PACK ITEM, del date, QTY, INSP DATE, VALUE ADTN, REMARKS (or REMARKS AS OF ...), BUYER
          </p>
        </div>

        {/* Upload Area */}
        {!preview && !result && (
          <div className="bg-white rounded-md border-2 border-dashed border-black/[0.1] p-12 text-center">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="excel-upload"
              data-testid="excel-file-input"
            />
            <label htmlFor="excel-upload" className="cursor-pointer block">
              <div className="w-16 h-16 rounded-full bg-[#F5F5F5] flex items-center justify-center mx-auto mb-4">
                {uploading ? (
                  <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-7 h-7 text-[#6B7D5C]" />
                )}
              </div>
              <p className="text-sm font-semibold text-[#111111]">
                {uploading ? 'Parsing file...' : 'Click to select Excel file'}
              </p>
              <p className="text-xs text-[#A1A1AA] mt-1">.xlsx format only</p>
            </label>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="space-y-6">
            {/* Summary bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white rounded-md border border-black/[0.06] p-5">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-[#F5F5F5] flex items-center justify-center">
                    <FileSpreadsheet className="w-4 h-4 text-[#52525B]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">{preview.total_rows} rows</p>
                    <p className="text-[11px] text-[#A1A1AA]">Total valid</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center">
                    <Plus className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-700">{preview.creates}</p>
                    <p className="text-[11px] text-[#A1A1AA]">New orders</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-700">{preview.updates}</p>
                    <p className="text-[11px] text-[#A1A1AA]">Updates</p>
                  </div>
                </div>
                {preview.errors?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-red-700">{preview.errors.length}</p>
                      <p className="text-[11px] text-[#A1A1AA]">Errors</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={reset} className="rounded-md" data-testid="cancel-upload-btn">Cancel</Button>
                <Button
                  onClick={handleConfirm}
                  disabled={confirming || preview.total_rows === 0}
                  className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md btn-premium"
                  data-testid="confirm-upload-btn"
                >
                  {confirming ? 'Processing...' : `Confirm & Process`}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* New buyers auto-create info */}
            {preview.new_buyers?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start gap-3" data-testid="new-buyers-info">
                <UserPlus className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">New Buyer Accounts Will Be Auto-Created</p>
                  <p className="text-xs text-blue-700 mt-1">
                    These buyers don't exist in the system yet. They will be automatically created as buyer accounts on confirm.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {preview.new_buyers.map(b => (
                      <span key={b} className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-100 text-xs font-semibold text-blue-800">{b}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Errors */}
            {preview.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm font-semibold text-red-800 mb-2">Row Errors (these rows will be skipped)</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {preview.errors.map((err, i) => (
                    <p key={`err-${i}-${err.slice(0, 20)}`} className="text-xs text-red-700">{err}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Table */}
            <div className="bg-white rounded-md border border-black/[0.06] overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-black/[0.04] hover:bg-transparent">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 w-20">Action</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">PO</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Style Name</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">PA NO</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Dept</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">QTY</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Del Date</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Buyer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(preview.preview || []).map((row, i) => (
                    <TableRow key={row.po || `preview-${i}`} className="border-b border-black/[0.03]">
                      <TableCell>
                        {row.action === 'create' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-[11px] font-semibold text-emerald-700">
                            <Plus className="w-3 h-3" /> New
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-[11px] font-semibold text-blue-700">
                            <RefreshCw className="w-3 h-3" /> Update
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-[#0A0A0A]">{row.po}</TableCell>
                      <TableCell className="text-sm text-[#52525B] max-w-[200px] truncate">{row.style_name}</TableCell>
                      <TableCell className="text-sm text-[#52525B]">{row.pa_no}</TableCell>
                      <TableCell className="text-sm text-[#52525B]">{row.department}</TableCell>
                      <TableCell className="text-sm font-semibold text-[#0A0A0A]">{row.quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-xs text-[#52525B]">
                        {row.delivery_date ? new Date(row.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-[#0A0A0A]">{row.buyer_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.total_rows > 50 && (
                <div className="text-center py-3 border-t border-black/[0.04]">
                  <p className="text-xs text-[#A1A1AA]">Showing first 50 of {preview.total_rows} rows</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-md border border-black/[0.06] p-8 text-center" data-testid="upload-result">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#111111]">Upload Complete</h2>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div>
                <p className="text-2xl font-semibold text-emerald-600">{result.created}</p>
                <p className="text-xs text-[#A1A1AA] mt-0.5">Created</p>
              </div>
              <div className="w-px h-10 bg-black/[0.06]" />
              <div>
                <p className="text-2xl font-semibold text-blue-600">{result.updated}</p>
                <p className="text-xs text-[#A1A1AA] mt-0.5">Updated</p>
              </div>
              {result.skipped > 0 && (
                <>
                  <div className="w-px h-10 bg-black/[0.06]" />
                  <div>
                    <p className="text-2xl font-semibold text-amber-600">{result.skipped}</p>
                    <p className="text-xs text-[#A1A1AA] mt-0.5">Skipped</p>
                  </div>
                </>
              )}
            </div>
            {result.buyers_auto_created?.length > 0 && (
              <div className="mt-4 bg-blue-50 rounded-md p-3 text-left">
                <p className="text-xs font-semibold text-blue-800 mb-1">Buyer accounts auto-created:</p>
                <div className="flex flex-wrap gap-2">
                  {result.buyers_auto_created.map((b) => (
                    <span key={b} className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 text-xs font-semibold text-blue-800">{b}</span>
                  ))}
                </div>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-4 bg-red-50 rounded-md p-3 text-left max-h-32 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <p key={`result-err-${i}-${err.slice(0, 20)}`} className="text-xs text-red-700">{err}</p>
                ))}
              </div>
            )}
            <Button onClick={reset} className="mt-6 bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md btn-premium" data-testid="upload-another-btn">
              Upload Another File
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
